import L from 'leaflet';

/** Minimal event shape the map needs. */
export interface MapEvent {
  id: string;
  name: string;
  status?: string;
  endDate?: string;
  location?: {
    city?: string;
    latitude?: number;
    longitude?: number;
  } | null;
}

export interface PlacedEvent {
  event: MapEvent;
  lat: number;
  lng: number;
}

/**
 * Dark (but not pitch-black) basemap. CARTO "Dark Matter" raster tiles are
 * keyless and reliable; a brightness lift in CSS turns the near-black base into
 * a readable charcoal — like the iOS app's dark map.
 */
export const CARTO_DARK_URL =
  'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';

export const CARTO_ATTRIBUTION = '© OpenStreetMap © CARTO';

export function addGatherTiles(map: L.Map): void {
  L.tileLayer(CARTO_DARK_URL, {
    attribution: CARTO_ATTRIBUTION,
    subdomains: 'abcd',
    maxZoom: 20,
    detectRetina: true,
  }).addTo(map);
}

/** Clean SVG map pin in the brand purple with a white centre. */
const PIN_SVG = `
<svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
  <path d="M15 1C7.8 1 2 6.8 2 14c0 9 13 25 13 25s13-16 13-25C28 6.8 22.2 1 15 1Z"
        fill="#9B5DE5" stroke="#ffffff" stroke-width="2.5"/>
  <circle cx="15" cy="14" r="5" fill="#ffffff"/>
</svg>`;

export function gatherPin(): L.DivIcon {
  return L.divIcon({
    className: 'wa-pin',
    html: PIN_SVG,
    iconSize: [30, 40],
    iconAnchor: [15, 38],
    popupAnchor: [0, -34],
  });
}

/** Cluster bubble: purple circle, white ring, count. */
export function gatherClusterIcon(count: number): L.DivIcon {
  const size = count >= 100 ? 46 : count >= 10 ? 40 : 36;
  return L.divIcon({
    className: 'wa-cluster',
    html: `<span class="wa-cluster__bubble">${count}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/**
 * Scatter events sitting on (almost) the same coordinate into a small spiral,
 * so co-located events separate into individual pins when you zoom in.
 */
export function scatterColocated(events: MapEvent[]): PlacedEvent[] {
  const groups = new Map<string, MapEvent[]>();
  for (const e of events) {
    const lat = e.location?.latitude;
    const lng = e.location?.longitude;
    if (typeof lat !== 'number' || typeof lng !== 'number') continue;
    const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
    const arr = groups.get(key);
    if (arr) arr.push(e);
    else groups.set(key, [e]);
  }

  const GOLDEN = 2.399963;
  const BASE = 0.00035;
  const out: PlacedEvent[] = [];

  for (const group of Array.from(groups.values())) {
    const lat0 = group[0].location!.latitude!;
    const lng0 = group[0].location!.longitude!;
    if (group.length === 1) {
      out.push({ event: group[0], lat: lat0, lng: lng0 });
      continue;
    }
    const lngScale = 1 / Math.max(0.1, Math.cos((lat0 * Math.PI) / 180));
    group.forEach((e: MapEvent, i: number) => {
      const angle = i * GOLDEN;
      const radius = BASE * Math.sqrt(i);
      out.push({
        event: e,
        lat: lat0 + radius * Math.cos(angle),
        lng: lng0 + radius * Math.sin(angle) * lngScale,
      });
    });
  }
  return out;
}
